// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 11-observability.md#audit-log
// Turns an audit row's machine-readable (action, resourceId) into one
// human-readable line — the scannable lead the table shows instead of a raw
// `<surface>.<action>.<state>` constant + a typed id stack. The raw fields stay
// available in the row-detail drawer. Pure and framework-free.

interface SummaryInput {
  readonly action: string;
  readonly resourceId: string | null;
}

// Exact-action templates. `r` is the resourceId (the object acted on); templates
// that don't need it ignore it. Keyed on the full `<surface>.<action>.<state>`.
const TEMPLATES: Record<string, (r: string) => string> = {
  'proxy.request.completed': (r) => `Proxied a request through ${r}`,
  'proxy.request.denied': (r) => `Blocked a proxied request to ${r}`,
  'auth.login.succeeded': () => 'Signed in',
  'auth.check.allowed': (r) => `Access allowed · ${r}`,
  'auth.check.denied': (r) => `Access denied · ${r}`,
  'mcp.tool.called': (r) => `Called ${r}`,
  'mcp.tool.denied': (r) => `Blocked MCP tool ${r}`,
  'pipeline.check.completed': (r) => `Check passed · ${r}`,
  'pipeline.check.failed': (r) => `Check failed · ${r}`,
  'pipeline.opa.evaluated': () => 'OPA policy evaluated',
  'permission.requested': (r) => `Requested ${r}`,
  'permission.approved': (r) => `Approved ${r}`,
  'permission.revoked': (r) => `Revoked ${r}`,
  'app.env.set': (r) => `Set env var ${r}`,
  'app.env.revealed': (r) => `Revealed env var ${r}`,
  'app.deploy.available': (r) => `Deployed ${r}`,
  'app.deploy.failed': (r) => `Deploy failed · ${r}`,
  'app.suspended': () => 'Paused the app',
  'app.restored': () => 'Restored the app',
  'rbac.group.changed': (r) => `Changed RBAC group ${r}`,
  'app.kill_switch.engaged': () => 'Engaged kill switch',
  'resource.database.provisioned': (r) => `Provisioned database ${r}`,
  'resource.secret.rotated': (r) => `Rotated secret ${r}`,
};

// "some.new_thing.happened" → "Some new thing happened" — a readable fallback for
// any action without a template, so unknown events never regress to raw jargon.
function humanizeAction(action: string): string {
  const words = action.replace(/[._]/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** One human-readable line describing what an audit event did. */
export function auditSummary(event: SummaryInput): string {
  const template = TEMPLATES[event.action];
  const text = template
    ? template(event.resourceId ?? '')
    : event.resourceId
      ? `${humanizeAction(event.action)} · ${event.resourceId}`
      : humanizeAction(event.action);
  // Drop a dangling separator if a template expected a resourceId that was absent.
  return text.replace(/\s*·\s*$/, '').trimEnd();
}
